/**
 * The mini-game duel seam: an interface and a registry, not a game.
 *
 * A duel fires when two evenly matched players collide (see `isEvenlyMatched`), and *which*
 * mini-game decides it is a lookup, never a branch in the collision path. Rock-paper-scissors is
 * the first entry in the registry; adding a second one is adding a file here and nothing else.
 *
 * This lives in `@chompdome/shared` rather than in `@chompdome/game-core` because both ends need
 * it and they need different halves: the simulation reads `compare` and `autoChoice` to decide a
 * duel, the browser reads `choices` to draw the buttons. Putting the rules anywhere else would
 * mean one side hard-coding what the other side enforces.
 *
 * Everything here is pure data and pure functions. `autoChoice` takes a float instead of a
 * generator so this module needs no PRNG of its own — the caller passes a draw from the room's
 * seeded stream, which is what keeps a timed-out duel replayable.
 */

import type { DuelOutcomeReason } from '../types/index.js';

/**
 * How one choice is *drawn*, as data rather than as a component.
 *
 * The overlay wanted real icons instead of three words, and the one place that must not learn
 * about rock-paper-scissors is the overlay: a second mini-game is a file in this directory plus
 * an entry in `MINI_GAMES`, and nothing else (CLAUDE.md, "The registry is the extension point").
 * So the artwork hangs off the choice, beside the label the buttons already read.
 *
 * It is paths and nothing else — no colours, no gradients, no sizes and no `<svg>` markup. This
 * package is shared by three processes and two of them have no DOM at all, so what travels is a
 * viewBox and a list of path commands; the renderer supplies the box and paints everything in
 * `currentColor`, which is the whole of "it reads correctly in both themes". Nothing here is
 * fetched: the paths are inlined at build time with the rest of the module.
 */
export interface MiniGameChoiceArt {
  /** The coordinate space every path below is written in, e.g. `'0 0 32 32'`. */
  readonly viewBox: string;
  /**
   * The outlines, in draw order, as SVG path commands.
   *
   * Outlines rather than solids, and no per-path paint: a filled silhouette disappears into a
   * light card and shouts on a dark one, where one stroke in the caller's own `currentColor`
   * reads the same on both.
   */
  readonly paths: readonly string[];
  /** Outline width, in viewBox units. */
  readonly strokeWidth?: number;
}

/** One thing a player may pick. `id` is the wire value; the rest is presentation. */
export interface MiniGameChoice {
  /** Short, because it rides the 20 Hz input frame. Bounded by MAX_MINI_GAME_CHOICE_LENGTH. */
  readonly id: string;
  readonly label: string;
  /** A single glyph, so a phone-sized button can be read at a glance. */
  readonly glyph: string;
  /**
   * The drawing a thumb-sized button shows instead of the glyph, when this game has one.
   *
   * Optional, because a mini-game is allowed to ship without artwork and because a client one
   * version ahead of the registry it is reading must still draw *something* — every renderer
   * falls back to `glyph`, which is text and therefore always available.
   */
  readonly art?: MiniGameChoiceArt;
}

/** Who won a single round: 1 for the first argument, -1 for the second, 0 for a draw. */
export type MiniGameVerdict = 1 | 0 | -1;

/**
 * One side's answer, as a resolver sees it.
 *
 * `choice` is what the duel stored and `lockedAtTick` is *when* it stored it, which is the whole
 * reason this type exists: `compare(a, b)` takes two strings and cannot see timing at all, so a
 * game whose answer is a moment rather than a pick — a nerve game where the later grab wins —
 * has nothing to decide on. Nothing here is state this package owns; it is a read-only view the
 * caller assembles from the duel it is already holding.
 *
 * **`lockedAtTick` IS NULL FOR EVERY GAME IN THE REGISTRY TODAY**, and deliberately: no duel
 * records the tick a side answered on, because nothing has ever needed it. Adding that field is
 * new state on `DuelSide` and therefore a new field in `world-hash.ts` — it would decide a later
 * tick's payout — which is a card of its own. This one widens the seam and stops there.
 */
export interface MiniGameAnswer {
  /** The choice this side locked, or null if it never answered. */
  readonly choice: string | null;
  /** The tick the choice was locked on, or null when the duel does not record one. */
  readonly lockedAtTick: number | null;
  /** True when the answer came from the timeout draw rather than from the player. */
  readonly auto: boolean;
}

/**
 * The per-duel values a resolver may read that nobody outside the process ever may.
 *
 * **THIS STRUCT IS NEVER SERIALIZED, AND THAT IS WHAT IT IS FOR.** A pot declares the *range* its
 * arm point is drawn from and every build carries that range as a constant; the point this duel
 * actually rolled is drawn once from the room's seeded stream, lives on `DuelState`, and reaches
 * exactly one reader — the game's own resolver, through here. There is no field for it on
 * `DuelSnapshot`, neither codec writes one, and `toDuelSnapshot` has nowhere to put it, which is
 * the same containment `DuelState.bustAtTick` already has and for a harder reason: a client that
 * knew where the arm was would grab one tick past it every time and the penalty band would stop
 * existing.
 *
 * It is passed **on the round** rather than handed to the game as a second argument because a
 * round is already "everything the duel knows about this decision" and a resolver is already pure
 * over it. The alternative — `RoomSim` reading the arm itself and deciding who pays — is one
 * `if (gameId === 'chicken')` in the duel phase, which is precisely the branch the registry
 * exists to avoid. The rule stays in the file that owns the game.
 */
export interface MiniGameRoundSecrets {
  /**
   * The tick this duel's pot arms on, or null for a duel with no pot.
   *
   * A grab strictly before it is early. Null for every game that declares no pot, which is what
   * makes "there is no early band" the honest reading rather than a special case.
   */
  readonly armAtTick: number | null;
}

/** Both answers, in the duel's own `a` / `b` order. A resolver reports on the first argument. */
export interface MiniGameRound {
  readonly a: MiniGameAnswer;
  readonly b: MiniGameAnswer;
  /**
   * The hidden per-duel thresholds, when the duel has any. Absent is the same as "no arm point":
   * a game that reads this must treat a missing struct as a duel with nothing to be early of.
   */
  readonly secrets?: MiniGameRoundSecrets;
}

/**
 * What a resolver answers: who won, and why.
 *
 * **A VERDICT ALONE IS NOT ENOUGH ANY MORE, WHICH IS WHY `resolve` RETURNS THIS RATHER THAN A
 * `MiniGameVerdict`.** A push-your-luck game has two different losses — the other player grabbed
 * first, or you grabbed before the pot armed — and they pay *different amounts in different
 * directions*. The duel cannot tell them apart from a verdict, and the client cannot explain
 * either of them from one. Widening the return type keeps the whole decision in the game's own
 * module, which is the same argument `resolve` was added under; the alternative was a second
 * optional method returning the half the first one could not express, which is two seams for one
 * decision.
 *
 * `compare`'s contract still binds the verdict: antisymmetric in `a` / `b`, and 0 for a round
 * neither side won.
 */
export interface MiniGameRoundResult {
  readonly verdict: MiniGameVerdict;
  /** Null for a game whose outcome needs no explaining. See `DuelOutcomeReason`. */
  readonly reason: DuelOutcomeReason | null;
}

/**
 * A POT: a stake that climbs on the duel's own clock and bursts at a point neither player knows.
 *
 * Declared by a game and read by the duel phase, exactly as `secretUntilLocked` and `resolve`
 * are, and it is the third member of this seam that is *data rather than a branch*. The duel
 * phase asks "does the running game have a pot" and never "is this chicken" — which is the same
 * line `ProjectileSpec` draws in `@chompdome/shared/powerups`, where the simulation branches on
 * a class of effect and never on a registry id. A second push-your-luck game is another entry
 * with a `pot` on it and no change to `RoomSim` at all.
 *
 * Everything here is pure data and one pure function. The *point* the pot bursts at is not on
 * this interface and must never be: it is drawn per duel from the room's seeded stream and lives
 * on the duel, because it is the one value in the whole mechanic that must never leave the
 * process. See `DuelState.bustAtTick` in `@chompdome/game-core`.
 */
export interface MiniGamePot {
  /**
   * How many ticks one percentage point of the pot takes.
   *
   * A game derives this from the duel's own answering window rather than choosing it, so the pot
   * reaching 100% and the window closing are the same instant by construction.
   */
  readonly ticksPerPercent: number;
  /**
   * The range the hidden bust point is drawn uniformly from, in percent — `[min, max)`, because
   * the draw is `min + roll * (max - min)` over a roll in [0, 1).
   *
   * A pot that could burst at 0% would be a game decided before either player could read it, and
   * one that could reach the ceiling would be a game the duel's own timeout had to finish.
   */
  readonly bustMinPercent: number;
  readonly bustMaxPercent: number;
  /**
   * The range the hidden ARM point is drawn uniformly from, in percent — `[min, max)`, exactly as
   * the bust range above is, and read by exactly the same two kinds of reader.
   *
   * A grab strictly before the point this duel drew is **early**: the grabber loses and pays
   * `earlyGrabPenalty` to the other player instead of either of them taking a pot. So the arm is
   * the bust's mirror image — the bust punishes holding too long, the arm punishes grabbing too
   * soon — and between them there is deliberately **no safe corridor**: a pot whose arm range ends
   * where its bust range begins is one where every point on the bar trades one risk against the
   * other, and there is no free grab anywhere on it.
   *
   * **THE RANGE IS PUBLISHABLE AND THE DRAWN POINT IS NOT**, which is `bustMinPercent`'s rule
   * exactly. These two numbers are shared constants every build carries and are identical for
   * every duel, so an overlay may mark the band on its dial and a bot may reason about it; the
   * tick *this* pot arms on is drawn per duel, rides `MiniGameRoundSecrets`, and is on no frame.
   */
  readonly armMinPercent: number;
  readonly armMaxPercent: number;
  /**
   * What an early grab costs its grabber, as a MULTIPLE OF THE DUEL'S ORDINARY STAKE — paid to
   * the *other* player.
   *
   * The same unit `share` returns, deliberately: the pot is already expressed as a multiple of
   * whichever stake this duel was playing for (a share of the loser for a contact duel, the
   * absolute number a gun fixed for a gun duel), so a penalty in the same unit needs no second
   * rule about which stake it is measured in and a gun duel's inflated stake carries through to
   * it by construction.
   *
   * **FLAT, NOT SCALED BY HOW EARLY.** A penalty that grew as the grab got earlier would flatten
   * the expected-value curve into a smooth slope; a flat one leaves the curve with the shape the
   * game is actually about — see the EV table on `chickenPotShare`.
   */
  readonly earlyGrabPenalty: number;
  /**
   * What the pot is worth at `percent`, as a MULTIPLE OF THE DUEL'S ORDINARY STAKE.
   *
   * A multiplier rather than a mass, because a duel's stake is already decided two different ways
   * — a share of the loser for a contact duel, an absolute number fixed at the start for a gun
   * duel — and a pot that returned a mass would have to know which. Pure, and total over
   * [0, 100]: it is called at resolution, where a throw would take the room's tick down with it.
   */
  share(percent: number): number;
}

/**
 * The prose an instructions page reads out, declared by the game rather than written about it.
 *
 * **THIS IS THE REGISTRY BEING THE EXTENSION POINT ONE SURFACE FURTHER OUT.** The web client's
 * How-to-play modal generates one page per entry in `MINI_GAMES` — the same rule `DuelOverlay`
 * and `duel-pot.ts` already follow, and the rule the power-ups page has always been under. A
 * hand-written list of games on that page is precisely the drift this seam exists to remove: a
 * third mini-game would be playable and undocumented, and nothing would fail.
 *
 * It is **words and nothing else** — no markup, no components, no colours. This package is read
 * by three processes and two of them have no DOM at all, which is `MiniGameChoiceArt`'s own rule
 * one field along: what travels is the sentence, and the renderer decides what it looks like.
 *
 * The *pictures* deliberately do not live here and must not: the choice artwork is already on
 * `MiniGameChoice.art` and a pot's ladder is already derivable from `share()` (see
 * `miniGamePotRungs`), so the instructions page mounts the same components the overlay does
 * rather than describing them. A screenshot is wrong the first time a rung moves.
 */
export interface MiniGameHowToPlay {
  /** One sentence: what this game is, to somebody who has never been frozen into one. */
  readonly summary: string;
  /**
   * The rules, one short line each, in the order they matter.
   *
   * Short because they are read on a phone held sideways beside a live example, and because a
   * paragraph is a thing a player skips at exactly the moment they were going to learn the game.
   */
  readonly rules: readonly string[];
}

export interface MiniGame {
  /** Stable across versions — it is on the wire and clients look their UI up by it. */
  readonly id: string;
  readonly name: string;
  /** Every legal choice, in display order. The simulation validates against exactly this list. */
  readonly choices: readonly MiniGameChoice[];
  /**
   * Compare two locked choices. Must be antisymmetric (`compare(a, b) === -compare(b, a)`) and
   * must return 0 for equal choices, or the duel has no reproducible winner.
   */
  compare(a: string, b: string): MiniGameVerdict;
  /**
   * Decide a round from everything the duel knows about both answers, timing included.
   *
   * **OPTIONAL, AND THE DEFAULT IS `compare`.** A game that omits it is resolved through
   * `resolveMiniGameRound`, which reads the two choices and calls `compare` — so rock-paper-
   * scissors declares nothing here and behaves exactly as it always did, byte for byte.
   *
   * It is a second *method on one interface* rather than a discriminated `kind: 'choice' |
   * 'race'`, and that is the decision this seam turns on:
   *
   *   - A union makes `MiniGame` a sum type, so every reader that touches `compare`, `choices`
   *     or `autoChoice` today — the duel's resolution, the input validator, the bot's answer,
   *     the overlay's buttons, across three packages — has to narrow before it may read a field.
   *     That is a change everywhere for a game that does not exist yet. An optional method is
   *     additive: nothing that compiles today stops compiling.
   *   - The two shapes are not disjoint anyway. A timing game still has `choices` (the grab is
   *     an id on the ordinary input frame), still needs `autoChoice` for a side that never
   *     answered, and may still want `compare` as the tie-break when two answers land on the
   *     same tick. `kind` would force a game to be one or the other when it is genuinely both.
   *   - "The registry is the extension point" (CLAUDE.md, "Mini-game duels") means which game
   *     decides a duel is a lookup and never a branch. A discriminated union is a branch by
   *     construction and would put a `switch (game.kind)` back into the duel path.
   *
   * Must obey `compare`'s contract: antisymmetric in `a` / `b`, and 0 for a round neither side
   * won. It is pure — the round is all it may read.
   *
   * It answers a `MiniGameRoundResult` rather than a bare verdict, because a game with a pot has
   * two different losses that pay different amounts in different directions and a verdict cannot
   * say which one happened. See `MiniGameRoundResult`.
   */
  resolve?(round: MiniGameRound): MiniGameRoundResult;
  /**
   * The choice a player who never answered is given. `roll` is a draw in [0, 1) from the room's
   * seeded stream: the auto-pick is decided server-side and replayably, never client-side and
   * never from `Math.random()`.
   */
  autoChoice(roll: number): string;
  /**
   * Whether a locked choice stays off the wire until the duel reveals.
   *
   * **THIS IS THE ONE PLACE THE SECRECY RULE IS NOW DECLARED**, and it used to be a global one:
   * `toDuelSnapshot` withheld both choices from every viewer, both duellists included, until the
   * duel reached `revealed`. For rock-paper-scissors that is a correctness requirement — a
   * client that could read its opponent's pick off its own socket would win every duel it
   * entered — and it stays exactly that, because this defaults to `true` and rps declares it.
   *
   * A nerve game wants the opposite and is not being sloppy about it: the whole tension is
   * watching the other player flinch, so a grab has to be public the instant it happens. That is
   * a property of the *game*, not of the transport, which is why it is declared here rather than
   * decided by the one function that enforces it.
   *
   * Undefined means `true`, so a game that never heard of this field keeps the safe behaviour.
   * Read it through `miniGameKeepsChoicesSecret`, never directly, so the default lives once.
   */
  readonly secretUntilLocked?: boolean;
  /**
   * The pot this game plays for, when it has one. Absent for a game that simply asks a question.
   *
   * **OPTIONAL, FOR `resolve`'s REASON**: a union would make `MiniGame` a sum type and force
   * every reader that touches `choices`, `compare` or `autoChoice` to narrow before it may read a
   * field, for a shape only one game has. Read it through `miniGamePotOf`, never directly, so
   * "does this duel have a pot" is asked in one spelling.
   *
   * A game that declares one is declaring three things at once and the duel phase reads all
   * three: that its duel ends when the pot bursts rather than when the answering window closes,
   * that a side which never answered has *said something* and must not be answered for, and that
   * a round nobody won leaves mass on the floor rather than leaving it where it was.
   */
  readonly pot?: MiniGamePot;
  /**
   * What an instructions page says about this game. Absent for a game that has not written any.
   *
   * **OPTIONAL, FOR `resolve`'s AND `pot`'s REASON**: an additive member is one nothing that
   * compiles today has to narrow for, and a fixture game in a test is allowed to be one line.
   * What makes the option safe rather than a hole is that `minigames.test.ts` asserts every
   * *registered* game declares one — the same discipline the secrecy declaration is under, where
   * the default exists and no entry in the registry is permitted to fall through to it.
   *
   * Read it through `miniGameHowToPlay`, never directly, so the question is asked in one
   * spelling — `miniGamePotOf`'s rule.
   */
  readonly howToPlay?: MiniGameHowToPlay;
}

/**
 * Ceiling on a choice id, enforced by the protocol schema.
 *
 * The wire has to bound the field without knowing which mini-game is running, so the bound is on
 * the registry as a whole rather than on any one game's vocabulary; the simulation then rejects
 * anything that is not a choice of *this* duel's game.
 */
export const MAX_MINI_GAME_CHOICE_LENGTH = 16;

/** True when `choice` is one this game accepts. The only membership test the simulation uses. */
export function isMiniGameChoice(game: MiniGame, choice: string): boolean {
  for (const candidate of game.choices) {
    if (candidate.id === choice) return true;
  }
  return false;
}

/** Look up a choice for display. Returns undefined for a choice this game does not have. */
export function findMiniGameChoice(game: MiniGame, choice: string): MiniGameChoice | undefined {
  return game.choices.find((candidate) => candidate.id === choice);
}

/**
 * True when this game's choices must stay off the wire until the duel reveals.
 *
 * The default is the safe one and it lives here rather than at the enforcement site, so a game
 * that omits the declaration — every game written before the field existed — is secret.
 */
export function miniGameKeepsChoicesSecret(game: MiniGame): boolean {
  return game.secretUntilLocked !== false;
}

/**
 * Decide one round, through the game's own resolver when it declares one and through `compare`
 * when it does not.
 *
 * This is the seam a timing game plugs into, and it is the *only* place the fallback lives, so
 * "rps keeps working through the default" is a property of one function rather than a rule every
 * caller remembers. Its production caller is `duelResultFrom` in `@chompdome/game-core`, which
 * hands it a round with real `lockedAtTick` values and the duel's own hidden thresholds, and
 * takes back both halves of the answer.
 *
 * A side with no answer at all is a draw rather than a throw — `rockPaperScissors.compare`'s own
 * rule for a pair it cannot score, and for its reason: a duel that cannot be scored must still
 * end, and a throw here would take the room's tick down with it.
 */
export function resolveMiniGameRound(game: MiniGame, round: MiniGameRound): MiniGameRoundResult {
  if (game.resolve !== undefined) return game.resolve(round);
  const { choice: a } = round.a;
  const { choice: b } = round.b;
  // The default explains nothing, and there is nothing to explain: a game decided by comparing
  // two picks has exactly one way to end, so `outcome` is already the whole story and a reason
  // beside it would be a second field saying the same thing. See `DuelOutcomeReason`.
  if (a === null || b === null) return { verdict: 0, reason: null };
  return { verdict: game.compare(a, b), reason: null };
}

/** The largest a pot's climb can read. A percentage, so this is 100 and is not a knob. */
export const MINI_GAME_POT_MAX_PERCENT = 100;

/**
 * This game's pot, or undefined for a game that has none.
 *
 * The one spelling of the question, for `miniGameKeepsChoicesSecret`'s reason: every reader of an
 * optional member goes through one function so the answer cannot be asked two different ways in
 * two different packages.
 */
export function miniGamePotOf(game: MiniGame): MiniGamePot | undefined {
  return game.pot;
}

/**
 * This game's prose, or undefined for one that declares none.
 *
 * `miniGamePotOf`'s arrangement and for its reason: every reader of an optional member goes
 * through one function, so a page that draws it and a test that checks for it cannot ask the
 * question two different ways.
 *
 * A game with none still gets a page — its name, its choices drawn from `MiniGameChoice.art`,
 * and its pot's own ladder — which is what makes "the page renders whatever a game declares" a
 * property of the page rather than a promise about the registry.
 */
export function miniGameHowToPlay(game: MiniGame): MiniGameHowToPlay | undefined {
  return game.howToPlay;
}

/**
 * How high the pot has climbed `elapsedTicks` into the duel, as a whole percentage point.
 *
 * Floored rather than rounded, so a rung is reached on the first tick the climb is genuinely past
 * it and never a tick early, and clamped at both ends: a duel whose pot has already settled is
 * asked about a tick in its own past, and nothing may report more than a whole pot.
 */
export function miniGamePotPercentAt(pot: MiniGamePot, elapsedTicks: number): number {
  if (!(elapsedTicks > 0)) return 0;
  const percent = Math.floor(elapsedTicks / pot.ticksPerPercent);
  return percent > MINI_GAME_POT_MAX_PERCENT ? MINI_GAME_POT_MAX_PERCENT : percent;
}

/**
 * How many ticks into a duel this pot bursts, from one draw in [0, 1).
 *
 * `roll` is a draw from the room's seeded stream rather than a generator, for `autoChoice`'s
 * stated reason: this module needs no PRNG of its own and a bust point has to be reproducible on
 * every host replaying the room. At least one tick, so a pot can never burst on the tick it was
 * created — a duel nobody had a frame to read would be a duel nobody played.
 */
export function miniGamePotBustTicks(pot: MiniGamePot, roll: number): number {
  const percent = pot.bustMinPercent + roll * (pot.bustMaxPercent - pot.bustMinPercent);
  const ticks = Math.floor(percent * pot.ticksPerPercent);
  return ticks < 1 ? 1 : ticks;
}

/**
 * How many ticks into a duel this pot ARMS, from one draw in [0, 1).
 *
 * `miniGamePotBustTicks`' twin, field for field, and drawn from the same kind of source for the
 * same reason: a threshold has to be reproducible on every host replaying the room, so `roll` is
 * a draw from the room's seeded stream rather than a generator this module owns.
 *
 * **THERE IS NO FLOOR OF ONE HERE, UNLIKE THE BUST.** A pot that burst on the tick it was created
 * would be a duel nobody had a frame to read, which is what that floor prevents; an arm point of
 * zero is a perfectly good duel — it is the tail of the uniform draw in which no grab is early,
 * and clamping it up would quietly make the penalty band certain when the range says it is not.
 */
export function miniGamePotArmTicks(pot: MiniGamePot, roll: number): number {
  const percent = pot.armMinPercent + roll * (pot.armMaxPercent - pot.armMinPercent);
  const ticks = Math.floor(percent * pot.ticksPerPercent);
  return ticks < 0 ? 0 : ticks;
}

/**
 * One step up in a pot's bonus, DERIVED FROM THE CURVE rather than declared by a game.
 *
 * A `MiniGamePot` exposes `share(percent)` and says nothing at all about where its bonus steps
 * up, deliberately: the alternative is every reader importing the three constants that belong to
 * one game, which is exactly the branch this seam exists to avoid. So a rung is *found* — a
 * percentage point where the pot's value per point of climb jumps.
 */
export interface MiniGamePotRung {
  /** The first whole percentage point at which this bonus applies. */
  readonly percent: number;
  /** What a whole pot's worth of climb is multiplied by from here on. */
  readonly multiplier: number;
}

/**
 * The smallest jump in value-per-climb that counts as a rung.
 *
 * Comparing two floats a hundred times over is a hundred chances to read arithmetic noise as a
 * bonus; anything a player could see is orders of magnitude above this.
 */
const POT_RUNG_EPSILON = 1e-9;

/**
 * Derived rung tables, one per pot object.
 *
 * A pot is a module-level constant in the game that declares it, so this is a handful of entries
 * for the life of the process — and the walk below is a hundred `share` calls, which is cheap but
 * is not worth repeating on every render of a duel overlay or on every duel a room starts.
 */
const POT_RUNG_CACHE = new WeakMap<MiniGamePot, readonly MiniGamePotRung[]>();

/** What a whole pot's climb would be worth at this rate, i.e. the bonus applying at `percent`. */
function potValuePerClimb(pot: MiniGamePot, percent: number): number {
  return (pot.share(percent) * MINI_GAME_POT_MAX_PERCENT) / percent;
}

/**
 * Where this pot's bonus steps up, walked from the curve. Empty for a pot that simply climbs.
 *
 * **THIS IS THE ONE DERIVATION, AND IT LIVES HERE BECAUSE IT HAS TWO READERS.** The duel overlay
 * marks the rungs on its dial (`apps/web/src/game/hud/duel-pot.ts`, which delegates to this) and
 * the bot brain aims its grab at one (`packages/game-core/src/bot.ts`) — neither of which may
 * name a game. Two copies of a walk that has to agree with `share()` is two things to get wrong.
 *
 * The walk is over whole percentage points because that is the resolution the wire has: the pot
 * on `DuelSnapshot` is an integer, so a rung between two of them is a rung no player can ever be
 * standing on.
 */
export function miniGamePotRungs(pot: MiniGamePot): readonly MiniGamePotRung[] {
  const cached = POT_RUNG_CACHE.get(pot);
  if (cached !== undefined) return cached;

  const rungs: MiniGamePotRung[] = [];
  let previous = potValuePerClimb(pot, 1);
  for (let percent = 2; percent <= MINI_GAME_POT_MAX_PERCENT; percent += 1) {
    const rate = potValuePerClimb(pot, percent);
    if (rate > previous + POT_RUNG_EPSILON) rungs.push({ percent, multiplier: rate });
    previous = rate;
  }

  POT_RUNG_CACHE.set(pot, rungs);
  return rungs;
}
